'use client';

import React, { useState } from 'react';
import { useParams } from 'next/navigation';
import { ClickAwayListener, Search } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';
import { useProfile } from 'src/context/providers/ProfileProvider';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import useUserSettings from 'src/hooks/users/useUserSettings';

interface Payer {
  payer_name: string;
  payer_type: string;
}

const AllPayersSelect = () => {
  const { type } = useParams<{ type: string }>();

  const { settings: userSettings, updateSettings } = useUserSettings();
  const { entityDetails } = useProfile();

  const { data: reimbursementData } = useGetPayerMixReimbursement({
    providerId: entityDetails?.provider_id,
    location: type,
  });

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const groupedData = React.useMemo(() => {
    if (!reimbursementData?.results) return [];
    // Group by payer_type first, ensuring unique payer names
    return reimbursementData?.results?.reduce((acc, item) => {
      item.payers.forEach((payer) => {
        if (!acc[payer.payer_type]) {
          acc[payer.payer_type] = [];
        }
        // Check if payer with same name already exists in this group
        const exists = acc[payer.payer_type].some(
          (p) => p.payer_name.toLowerCase() === payer.payer_name.toLowerCase()
        );
        if (!exists) {
          acc[payer.payer_type].push(payer);
        }
      });
      return acc;
    }, {} as Record<string, any[]>);
  }, [reimbursementData]);

  const sortedPayers = React.useMemo(() => {
    return Object.entries(groupedData || {}).map(([type, payers]) => ({
      type,
      payers: (payers as Payer[])
        ?.filter((payer) =>
          payer.payer_name.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
        ?.sort((a, b) => a.payer_name.localeCompare(b.payer_name)),
    }));
  }, [groupedData, debouncedSearch]);

  const handlePayerChange = (payer: any) => {
    if (!payer) return;

    // Check if the site exists in the reimbursement data
    const siteDoesExist = reimbursementData?.results?.some((result) =>
      result.payers.some((p) => p.soc_type === userSettings?.selectedSite)
    );

    updateSettings({
      selectedPayer: payer,
      // If the site does not exist, use the payer's site
      selectedSite: siteDoesExist
        ? userSettings?.selectedSite
        : payer.soc_type ?? null,
    });
    setOpen(false);
    setSearch('');
  };

  const selectedPayer = userSettings?.selectedPayer ?? null;

  const hasOptions = React.useMemo(() => {
    return Object.values(groupedData || {}).some(
      (payers) => (payers as Payer[]).length > 0
    );
  }, [groupedData]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="relative">
        <button
          data-testid="all-payers-select-button"
          type="button"
          onClick={() => setOpen(!open)}
          disabled={!hasOptions}
          className="flex items-center gap-2 py-1.5 px-3 max-w-64 h-8 border border-neutral-300 rounded-lg bg-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-sm font-medium text-gray-700 ">Payer:</span>
          <span className="text-sm font-medium text-gray-700 overflow-hidden text-ellipsis">
            {hasOptions
              ? userSettings?.selectedPayer?.payer_name ?? 'All Payers'
              : 'All Payers'}
          </span>
          <Icon icon={faCaretDown} className="text-gray-500 shrink-0" />
        </button>
        {open && (
          <div className="absolute z-10 mt-1 min-w-48 max-h-64 overflow-y-auto bg-white border border-neutral-300 rounded-lg shadow-md">
            <div className="flex flex-col gap-2 p-4 border-b border-neutral-300">
              <label className=" text-sm font-semibold text-gray-600">
                Find a payer
              </label>
              <Search
                onSearch={setSearch}
                placeholder="Search..."
                size="sm"
                variant="outlined"
              />
            </div>
            <div className="flex flex-col gap-2 p-4">
              {sortedPayers.map(({ type, payers }) => {
                if (payers.length === 0) return null;
                return (
                  <div key={type} className="flex flex-col gap-2">
                    <div className="text-sm font-semibold text-gray-600">
                      {type}
                    </div>
                    {payers.map((payer, index) => (
                      <label
                        data-testid={`all-payers-select-option-${payer.payer_name}`}
                        key={index}
                        className="flex items-center gap-1 cursor-pointer whitespace-nowrap"
                        onClick={() => handlePayerChange(payer)}
                      >
                        <Icon
                          icon={faCheck}
                          size="sm"
                          className={
                            selectedPayer?.payer_name === payer.payer_name
                              ? 'text-sky-600'
                              : 'text-transparent'
                          }
                        />
                        <span className="text-sm text-gray-700">
                          {payer.payer_name}
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default AllPayersSelect;
